import { useState, useEffect, useRef } from "react"
import { useQueryState } from "nuqs"
import Dropdown from "../../components/Dropdown"
import { useAllCustomerFeedbacks } from "../../hooks/useCustomerFeedback"
import ApiError from "../../components/ApiError"
import Loader from "../../components/Loader"
import { getDateRange } from "../../utils/datetime"

const CustomerFeedback = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [activeSearchTerm, setActiveSearchTerm] = useQueryState("search", {
    defaultValue: "",
  })
  const [dateRange, setDateRange] = useQueryState("dateRange", {
    defaultValue: "This Month",
  })
  const [reportDelivery, setReportDelivery] = useQueryState("reportDelivery", {
    defaultValue: "All",
  })
  const sentinelRef = useRef(null)

  const getDateFilterValue = (range) => {
    if (range === "All") return ""
    const interval = getDateRange(range)
    return [interval.start.toISODate(), interval.end.toISODate()].join(",")
  }

  const booleanToLabel = (value, nullLabel) => {
    if (value === true)
      return {
        text: "Yes",
        color: "text-green-600 bg-green-50 border-green-200",
      }
    if (value === false)
      return { text: "No", color: "text-red-600 bg-red-50 border-red-200" }
    return {
      text: nullLabel,
      color: "text-gray-500 bg-gray-50 border-gray-200",
    }
  }

  const {
    data: { data, total } = { data: [], total: 0 },
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAllCustomerFeedbacks({
    limit: 20,
    sortBy: "CreatedAt:DESC",
    filters: activeSearchTerm?.trim()
      ? { AccessionNo: activeSearchTerm.trim() }
      : {
          CreatedAt: getDateFilterValue(dateRange),
          ReportDelivery: reportDelivery === "All" ? undefined : reportDelivery,
        },
  })

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      {
        rootMargin: "200px",
        threshold: 0.1,
      }
    )

    observer.observe(sentinel)

    return () => {
      observer.disconnect()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const dateRangeOptions = [
    "Today",
    "Yesterday",
    "This Week",
    "Last Week",
    "This Month",
    "Last Month",
    "All",
  ]

  const reportDeliveryOptions = [
    "All",
    "Doctor's office",
    "Email",
    "Patient portal",
    "Other",
  ]

  const dateFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }

  const handleClearFilters = () => {
    setSearchTerm("")
    setDateRange("This Month")
    setReportDelivery("All")
  }

  const handleClearSearch = () => {
    setSearchTerm("")
    setActiveSearchTerm("")
  }

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-2">Customer Feedback</h1>

      {/* Filter Row */}
      <div className="bg-gray-50 p-4 rounded-lg mb-2">
        <div className="flex gap-4">
          {/* Date Range */}
          <div>
            <Dropdown
              disabled={!!activeSearchTerm}
              label="DATE RANGE"
              options={activeSearchTerm ? [""] : dateRangeOptions}
              setValue={setDateRange}
              title={
                activeSearchTerm
                  ? "Clear the search field to enable filters"
                  : "Filter result according to date range"
              }
              value={activeSearchTerm ? "" : dateRange}
            />
          </div>
          {/* Report Delivery */}
          <div>
            <Dropdown
              disabled={!!activeSearchTerm}
              label="REPORT DELIVERY"
              options={activeSearchTerm ? [""] : reportDeliveryOptions}
              setValue={setReportDelivery}
              title={
                activeSearchTerm
                  ? "Clear the search field to enable filters"
                  : "Filter by how the report was received"
              }
              value={activeSearchTerm ? "" : reportDelivery}
            />
          </div>
          <div className="flex items-end justify-end">
            <button
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
              disabled={!!activeSearchTerm}
              title={
                activeSearchTerm
                  ? "Clear the search field to enable filters"
                  : "Clear all filters"
              }
              onClick={handleClearFilters}
            >
              Clear
            </button>
          </div>
          <div className="flex-1" />
          {/* Accession Number */}
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-xs font-medium mb-1 ml-1 text-gray-700">
                ACCESSION NUMBER
              </label>
              <input
                className="w-full min-w-[300px] px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g. AN-2026-00142"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    setActiveSearchTerm(searchTerm)
                  }
                }}
              />
            </div>
            <button
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:bg-blue-400"
              onClick={() => setActiveSearchTerm(searchTerm)}
            >
              Search
            </button>
            <button
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
              onClick={handleClearSearch}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="mt-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 gap-4">
            <Loader />
            <span className="text-gray-600">Loading feedback...</span>
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-500">
                Showing <span className="font-bold">{data.length}</span> of{" "}
                <span className="font-bold">{total}</span> feedback entries
              </span>
            </div>
            {data && data.length > 0 ? (
              <div className="space-y-2 max-h-[calc(100vh-325px)] overflow-auto">
                {data.map((feedback) => (
                  <div
                    key={feedback.id}
                    className="bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
                  >
                    {/* Header: accession + date */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                      <span className="font-semibold text-gray-800 text-sm">
                        Accession No:{" "}
                        {feedback.AccessionNo ?? (
                          <span className="text-gray-400 font-normal">N/A</span>
                        )}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(feedback.CreatedAt).toLocaleDateString(
                          "en-US",
                          dateFormatOptions
                        )}
                      </span>
                    </div>

                    {/* Fields grid */}
                    <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        {
                          label: "Illustrations helped with diagnosis",
                          ...booleanToLabel(
                            feedback.IllustrationsHelpful,
                            "N/A"
                          ),
                        },
                        {
                          label: "Would recommend report to others",
                          ...booleanToLabel(feedback.RecommendReport, "—"),
                        },
                        {
                          label: "Simplified Patient Explanation helpful",
                          ...booleanToLabel(
                            feedback.SpeHelpful,
                            "Didn't read it"
                          ),
                        },
                        {
                          label: "Received report via",
                          text: feedback.ReportDelivery || "—",
                          color: feedback.ReportDelivery
                            ? "text-blue-600 bg-blue-50 border-blue-200"
                            : "text-gray-400 bg-gray-50 border-gray-200",
                        },
                      ].map(({ label, text, color }) => (
                        <div key={label} className="flex flex-col gap-1">
                          <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                            {label}
                          </span>
                          <span
                            className={`text-xs font-semibold px-2.5 py-1 rounded-lg border self-start ${color}`}
                          >
                            {text}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Comments */}
                    {(feedback.Comments || true) && (
                      <div className="px-4 pb-3">
                        <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400 mb-1">
                          Comments
                        </div>
                        {feedback.Comments ? (
                          <p className="text-sm text-gray-600 whitespace-pre-line break-words">
                            {feedback.Comments}
                          </p>
                        ) : (
                          <p className="text-xs text-gray-400 italic">—</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Infinite Scroll Sentinel */}
                <div ref={sentinelRef} className="h-1" />

                {/* Load More Section - Fallback */}
                {hasNextPage && (
                  <div className="flex justify-center py-4">
                    {isFetchingNextPage ? (
                      <div className="flex items-center gap-2">
                        <Loader />
                        <span className="text-gray-600">
                          Loading more feedback...
                        </span>
                      </div>
                    ) : (
                      <button
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors"
                        disabled={isFetchingNextPage}
                        onClick={fetchNextPage}
                      >
                        Load More Feedback
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No feedback found. Try adjusting your filters.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default CustomerFeedback
